import React, { useState, useEffect } from 'react';
import * as api from '../../services/api';
import {
    Users,
    BookOpen,
    Target,
    Zap,
    ArrowUpRight
} from 'lucide-react';
import './Admin.css';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const data = await api.getAdminStats();
                setStats(data);
            } catch (err) {
                console.error('Fetch stats error:', err);
                setError(err.message || 'Failed to load statistics');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="spinner"></div></div>;
    if (error) return <div style={{ padding: '3rem', textAlign: 'center', color: '#dc2626', background: '#fef2f2', borderRadius: '1rem', border: '1px solid #fee2e2' }}>Error: {error}</div>;

    const { users, quizzes, performance } = stats;

    return (
        <>
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Command Center</h1>
                    <p>Real-time platform metrics and analytic overview</p>
                </div>
            </header>

            {/* Dashboard Stats Section */}
            <section className="admin-stats-card-grid">
                <div className="stat-card-base">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <p className="stat-card-label">Total Students</p>
                        <Users size={16} color="#94a3b8" />
                    </div>
                    <h3 className="stat-card-value">{users.totalStudents}</h3>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                        <span style={{ color: '#066af2' }}>🧩 SCR: {users.byTrack.scratch || 0}</span>
                        <span style={{ color: '#0ea5e9' }}>🌐 WEB: {users.byTrack.web || 0}</span>
                    </div>
                </div>

                <div className="stat-card-base">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <p className="stat-card-label">Active Modules</p>
                        <BookOpen size={16} color="#94a3b8" />
                    </div>
                    <h3 className="stat-card-value">{quizzes.totalQuizzes}</h3>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                        <span>🧩 SCR: {quizzes.byTrack.scratch?.total || 0}</span>
                        <span>🌐 WEB: {quizzes.byTrack.web?.total || 0}</span>
                    </div>
                </div>

                <div className="stat-card-base">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <p className="stat-card-label">Avg. Accuracy</p>
                        <Target size={16} color="#94a3b8" />
                    </div>
                    <h3 className="stat-card-value">{performance.averageScore}%</h3>
                    <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${performance.averageScore}%`, height: '100%', background: '#0f172a' }}></div>
                    </div>
                </div>

                <div className="stat-card-base">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <p className="stat-card-label">Success Rate</p>
                        <Zap size={16} color="#94a3b8" />
                    </div>
                    <h3 className="stat-card-value">{performance.passRate}%</h3>
                    <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8' }}>{performance.totalAttempts} TOTAL ATTEMPTS</p>
                </div>
            </section>

            {/* Recent Requests Table Section */}
            <section className="admin-table-section-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
                    <div>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.04em' }}>Latest Inquiries</h2>
                        <p style={{ fontSize: '0.95rem', color: '#64748b', marginTop: '0.25rem' }}>Direct pipeline of prospective student leads</p>
                    </div>
                    <button className="btn-action primary" onClick={() => window.location.href = '/admin/free-sessions'}>
                        Analyze Pipeline
                        <ArrowUpRight size={16} />
                    </button>
                </div>

                <div className="admin-table-wrapper">
                    {!stats.freeSessions?.total && stats.recentFreeSessions?.length === 0 ? (
                        <div style={{ padding: '5rem 0', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                            Currently no active inquiries in the intake pipeline.
                        </div>
                    ) : (
                        <table className="admin-standard-table">
                            <thead>
                                <tr>
                                    <th>Student Prospect</th>
                                    <th>Origin / Contact</th>
                                    <th>Schedule</th>
                                    <th>Protocol Status</th>
                                    <th>Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats.recentFreeSessions || []).map(session => (
                                    <tr key={session._id}>
                                        <td style={{ fontWeight: 800, color: '#0f172a' }}>{session.fullName}</td>
                                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>{session.phone}</td>
                                        <td>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{session.preferredTime || 'Flexible'}</div>
                                        </td>
                                        <td>
                                            <span className={`status-pill ${session.status === 'pending' ? 'status-pending' : 'status-active'}`}>
                                                {session.status === 'pending' ? 'Waiting' : session.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 600 }}>
                                            {new Date(session.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>
        </>
    );
}
